<script setup lang="ts">
import { useForm } from 'vee-validate';
import { onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';

import { Option } from 'lib/components/dropdown/Dropdown.vue.d';
import eventBus, { Events } from 'lib/event-bus';
import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import {
  getNestedPropertyValue,
  setNestedPropertyValue,
} from 'lib/utils/object.util';

import {
  FilterContainerEmits,
  FilterContainerProps,
  FilterContainerSlots,
  FilterField,
  FilterMatchMode,
  FilterOptions,
  LoadingFilters,
  MultiSelectFilterField,
} from './FilterContainer.vue.d';
import applyFilter, { applyStaticFilter } from './helpers/applyFilter.helper';
import Button from '../button/Button.vue';
import Calendar from '../calendar/Calendar.vue';
import { Data, QueryParams } from '../datatable/DataTable.vue.d';
import Dropdown from '../dropdown/Dropdown.vue';
import InputRangeNumber from '../inputrangenumber/InputRangeNumber.vue';
import MultiSelect from '../multiselect/MultiSelect.vue';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<FilterContainerProps>(), {
  tableName: 'datatable',
  fieldsPerRow: 4,
});

const emit = defineEmits<FilterContainerEmits>();

defineSlots<FilterContainerSlots>();

const { locale } = useWangsVueConfig('FilterContainer');

const { resetForm, handleSubmit, setValues } = useForm();
const loading = ref<LoadingFilters>({});
const filterOption = ref<FilterOptions>({});
const tableData = shallowRef<Data[]>();

const container = ref<HTMLDivElement | null>(null);
const showFilter = shallowRef(false);
const fieldsKey = shallowRef(0);

onMounted(() => {
  setGridFieldsLayout();

  eventBus.on('show-filter', updateShowFilter);
  eventBus.on('data-table:updated', assignTableData);
  setStaticMatchModeFields();
});

onBeforeUnmount(() => {
  eventBus.off('show-filter', updateShowFilter);
  eventBus.off('data-table:updated', assignTableData);
});

/**
 * I dont think this variable needs to be reactive
 * In the general use case, the field's visibility is not going to change,
 * only set once before the component mounted based on route or user role
 */
const visibleFields: FilterField[] = props.fields.filter(
  (field) => field.visible !== false,
);

const updateShowFilter = (e: Events['show-filter']): void => {
  if (e.tableName === props.tableName) {
    showFilter.value = e.show;
  }
};

const assignTableData = (e: Events['data-table:updated']): void => {
  if (e.tableName === props.tableName) tableData.value = e.data;
};

const getOptions = async (
  fn: MultiSelectFilterField<QueryParams>['fetchOptionFn'],
  field: string,
  queryParams: QueryParams = {},
): Promise<void> => {
  if (props.static && !fn) {
    filterOption.value[field] = getTablePopulatedOptions(field);
  } else
    try {
      loading.value[field] = true;
      const params = { [field]: true, ...queryParams };
      const optionOrResData = await fn?.(params);

      if ('data' in optionOrResData) {
        const { data } = optionOrResData;
        filterOption.value[field] = data.data[field];
      } else if (Array.isArray(optionOrResData))
        filterOption.value[field] = optionOrResData;
    } catch (error) {
      console.error(error);
    } finally {
      loading.value[field] = false;
    }
};

const getTablePopulatedOptions = (field: string): Option[] => {
  const uniqueValues = new Set<string>();

  if (tableData.value) {
    for (let i = 0; i < tableData.value.length; i++) {
      const fieldValue = getNestedPropertyValue(tableData.value[i], field);

      // Handle array return values from getNestedPropertyValue
      if (Array.isArray(fieldValue)) {
        fieldValue.forEach((value) => uniqueValues.add(String(value)));
      } else if (fieldValue !== undefined && fieldValue !== null) {
        uniqueValues.add(String(fieldValue));
      }
    }
  }

  return Array.from(uniqueValues)
    .filter(Boolean)
    .map((v) => ({ label: v, value: v }));
};

const setGridFieldsLayout = (): void => {
  if (container.value) {
    const { children } = container.value;
    const childCount = container.value.childElementCount;
    const isOdd = childCount % props.fieldsPerRow;

    let [rowPos, colPos] = [1, 1];
    for (const i in Array.from({ length: childCount })) {
      const isLast = Number(i) === childCount - 1;
      children[i].setAttribute(
        'style',
        `grid-area: ${rowPos}/${isLast && isOdd ? props.fieldsPerRow : colPos}`,
      );

      if (++colPos > props.fieldsPerRow) {
        colPos = 1;
        rowPos++;
      }
    }
  }
};

const clear = (): void => {
  resetForm();
  fieldsKey.value++;
};

const apply = handleSubmit((values) => {
  if (props.static)
    applyStaticFilter(values, props.tableName, visibleFields, emit);
  else applyFilter(values, props.tableName, emit);
});

const getMatchModeField = (field: FilterField, index?: number): string => {
  switch (field.type) {
    case 'rangenumber':
      if (field.field) return `${field.field}.matchMode`;
      return `${field.fields?.[index]}.matchMode`;
    case 'multiselect':
    case 'dropdown':
    case 'calendar':
    case 'category':
    case 'group':
      return `${field.field}.matchMode`;
  }
};

const getMatchModeValue = (field: FilterField): FilterMatchMode => {
  return ((): FilterMatchMode => {
    switch (field.type) {
      case 'rangenumber':
        return 'BETWEEN';
      case 'multiselect':
      case 'category':
      case 'group':
        return 'IN';
      case 'dropdown':
        return 'EQUALS';
      case 'calendar':
        return 'DATE_BETWEEN';
    }
  })();
};

const setStaticMatchModeFields = (): void => {
  if (props.static) {
    const matchModes: Record<string, FilterMatchMode> = {};

    visibleFields.forEach((field) => {
      setNestedPropertyValue(
        matchModes,
        getMatchModeField(field),
        getMatchModeValue(field),
      );
    });

    setValues(matchModes);
  }
};

const getValueField = (field: FilterField, index?: number): string => {
  switch (field.type) {
    case 'rangenumber':
      if (field.field)
        return props.static
          ? `${field.field}.value.${index}`
          : `${field.field}.${index}`;

      return props.static
        ? `${field.fields[index]}.value`
        : field.fields[index];

    case 'multiselect':
    case 'dropdown':
    case 'calendar':
    case 'category':
    case 'group':
      return props.static ? `${field.field}.value` : `${field.field}`;
  }
};

watch(() => props.fields, setGridFieldsLayout);
</script>

<template>
  <form
    ref="container"
    v-show="showFilter"
    v-bind="$preset.filtercontainer.root"
    :style="`grid-template-columns: repeat(${fieldsPerRow}, 1fr)`"
    @submit.prevent="apply"
    data-wv-name="filtercontainer"
  >
    <template
      :key="JSON.stringify(field) + fieldsKey"
      v-for="field of visibleFields"
    >
      <!-- Match Mode: BETWEEN -->
      <InputRangeNumber
        v-if="field.type == 'rangenumber'"
        v-bind="field"
        :mandatory="false"
        :max-field-name="getValueField(field, 1)"
        :min-field-name="getValueField(field, 0)"
        :show-optional-text="false"
        use-validator
      />

      <!-- Match Mode IN -->
      <MultiSelect
        v-else-if="field.type == 'multiselect'"
        v-bind="field"
        :field-name="getValueField(field)"
        :loading="loading[field.optionField ?? field.field]"
        :mandatory="false"
        :options="filterOption[field.optionField ?? field.field]"
        :show-optional-text="false"
        @show="
          getOptions(
            field.fetchOptionFn,
            field.optionField ?? field.field,
            field.params,
          )
        "
        hide-header-checkbox
        option-label="label"
        option-value="value"
        use-validator
      />

      <!-- MatchMode: EQUALS -->
      <Dropdown
        v-else-if="field.type == 'dropdown'"
        v-bind="field"
        :field-name="getValueField(field)"
        :loading="loading[field.field]"
        :mandatory="false"
        :options="filterOption[field.optionField ?? field.field]"
        :show-optional-text="false"
        @show="
          getOptions(
            field.fetchOptionFn,
            field.optionField ?? field.field,
            field.params,
          )
        "
        option-label="label"
        option-value="value"
        use-validator
      />

      <!-- Match Mode: DATE_BETWEEN -->

      <Calendar
        v-else-if="field.type === 'calendar'"
        v-bind="field"
        :field-name="getValueField(field)"
        :mandatory="false"
        :show-optional-text="false"
        selection-mode="range"
        use-validator
      />

      <slot
        v-else
        :field="field"
        :field-name="getValueField(field)"
        name="field"
      />
    </template>

    <div
      class="flex items-end justify-end gap-2"
      data-wv-section="filteractionbuttons"
    >
      <Button
        :label="locale.clearFieldText"
        @click="clear"
        data-wv-section="clearbutton"
        text
      />

      <Button
        :label="locale.applyText"
        @click="apply"
        data-wv-section="applybutton"
        severity="success"
      />
    </div>
  </form>
</template>
